import { useEffect } from "react";

import { i18n } from "@lingui/core";
import { useToasters } from "@mobsuccess-devops/react-ui/Toaster/use-toasters";
import { useMutation } from "@tanstack/react-query";

import { getEntrypointService } from "../../public/workspace/webapps";

const INTERVAL_30MIN = 1000 * 60 * 30;
const TOAST_ID = "version-drift-notification";
const LOCAL_REVISION = import.meta.env.VITE_AWS_COMMIT_ID;

type NotifyVersionDriftProps = {
  entrypoint: string;
};

export function NotifyVersionDrift({
  entrypoint,
}: NotifyVersionDriftProps): null {
  const { toast } = useToasters();
  const service = getEntrypointService(entrypoint);
  const { mutate: assertVersionDrift, submittedAt } = useMutation({
    async mutationFn() {
      try {
        const data = await (
          await fetch(
            new URL(`/dist-${service}/revision.txt`, window.location.href),
          )
        ).text();
        return data.trim();
      } catch {
        return null;
      }
    },
    onSuccess(remote) {
      if (!remote || remote === LOCAL_REVISION || toast.isVisible(TOAST_ID)) {
        return;
      }
      toast.info({
        id: TOAST_ID,
        duration: Number.POSITIVE_INFINITY,
        title: i18n._("version-drift.notification.title"),
        description: i18n._("version-drift.notification.description"),
        actions: [
          {
            id: "reload",
            palette: "info",
            variant: "outlined",
            label: i18n._("version-drift.notification.action-reload"),
            onClick() {
              window.location.reload();
            },
          },
        ],
      });
    },
  });

  useEffect(() => {
    if (!LOCAL_REVISION || !service) {
      return;
    }
    const timeout = setTimeout(assertVersionDrift, INTERVAL_30MIN);
    return () => {
      clearTimeout(timeout);
    };
  }, [service, assertVersionDrift, submittedAt]);

  useEffect(() => {
    if (!LOCAL_REVISION || !service) {
      return;
    }
    const handleFocus = () => {
      assertVersionDrift();
    };
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [service, assertVersionDrift]);

  return null;
}
